import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { get } from 'lodash';
import { VerifiedIdOrCodeStatusEnum } from '@nxsys/constants/AppEnums';
import {
  TOKEN,
  USER,
  PAYROLL_COUNTRY,
  POLICIES,
  ME_ACTIONS,
  ENFORCED_MODULES,
  APP_CONFIGS,
  ADMIN_LOGIN_AS,
  AUTH_CODE,
  AUTH_CODE_IS_BUREAU,
  IS_BUREAU,
  IS_ADMIN,
  IS_BUREAU_EMPLOYER,
  IS_AGENCY,
  IS_EMPLOYEE,
  PREVIOUS_AUTH_STATE,
  ALTERNATE_SESSION,
} from '@nxsys/store/constants/keys';
import { SESSION_EXPIRED_KEY } from '@nxsys/constants/session';
import { appAct } from '../action';
import { roleOf } from '../roleOf';
import { appConfigManager } from '@nxsys/utils/appConfigs';
import {
  readStoredActionAccess,
  toActionAccess,
} from '@nxsys/helpers/actionGate';
import {
  NotificationMessageType,
  PayrollCountryType,
} from '@nxsys/types/models/Common';
import { UserType } from '@nxsys/types/models/User';
import { SetLoginDataPayload } from '../types';
import { resolvePayrollCountry } from './resolvePayrollCountry';

export interface AppState {
  authCode: string;
  isBureau: boolean;
  isAdmin: boolean;
  isBureauEmployer: boolean;
  isAgency: boolean;
  isEmployee: boolean;
  token: string;
  user: UserType | null;
  payrollCountry: PayrollCountryType;
  appLoading: boolean;
  appConfigs: Record<string, any> | null;
  dashboardLoading: boolean;
  /** The screen policies from /me/policies, a full access as its screens. */
  policies: string[];
  /** Fine-grained actions from /me/actions. */
  actions: string[];
  /** Modules whose menus, routes and buttons go by `actions`. */
  enforcedModules: string[];
  messageNotification: NotificationMessageType | null;
  onboarding: any;
  prospect: any;
  acountList: any;
  prospectId: string;
  currentYearEndProcess: { [key: string]: any } | null | null;
  alternateSystem: { token: string; user: UserType; isBureau: boolean } | null;
}

const storedActionAccess = readStoredActionAccess();

export const initialState: AppState = {
  authCode: localStorage.getItem(AUTH_CODE)
    ? JSON.parse(localStorage.getItem(AUTH_CODE) as string)
    : '',
  isBureau: localStorage.getItem(IS_BUREAU) === 'true',
  isAdmin: localStorage.getItem(IS_ADMIN) === 'true',
  isBureauEmployer: localStorage.getItem(IS_BUREAU_EMPLOYER) === 'true',
  isAgency: localStorage.getItem(IS_AGENCY) === 'true',
  isEmployee: localStorage.getItem(IS_EMPLOYEE) === 'true',
  token: localStorage.getItem(TOKEN) || '',
  user: JSON.parse(localStorage.getItem(USER) || '{}') || null,
  payrollCountry: (localStorage.getItem(PAYROLL_COUNTRY) ||
    '') as PayrollCountryType,
  appLoading: false,
  appConfigs: null as Record<string, any> | null,
  dashboardLoading: false,
  policies: JSON.parse(localStorage.getItem(POLICIES) || '[]') || [],
  actions: storedActionAccess.actions,
  enforcedModules: storedActionAccess.enforcedModules,
  messageNotification: null,
  onboarding: {},
  prospect: {},
  acountList: {},
  prospectId: '',
  currentYearEndProcess: null,
  alternateSystem: localStorage.getItem(ALTERNATE_SESSION)
    ? JSON.parse(localStorage.getItem(ALTERNATE_SESSION) as string)
    : null,
};
const applyRoleFlags = (state: AppState, user: any) => {
  if (user?.roles === 'subcontractor') {
    state.isEmployee = true;
    localStorage.setItem(IS_EMPLOYEE, 'true');
  } else {
    state.isEmployee = false;
    localStorage.setItem(IS_EMPLOYEE, 'false');
  }

  if (user?.roles === 'company') {
    state.isBureauEmployer = true;
    localStorage.setItem(IS_BUREAU_EMPLOYER, 'true');
  } else {
    state.isBureauEmployer = false;
    localStorage.setItem(IS_BUREAU_EMPLOYER, 'false');
  }

  if (user?.roles === 'agency') {
    state.isAgency = true;
    localStorage.setItem(IS_AGENCY, 'true');
  } else {
    state.isAgency = false;
    localStorage.setItem(IS_AGENCY, 'false');
  }

  if (user?.roles === 'admin') {
    state.isAdmin = true;
    localStorage.setItem(IS_ADMIN, 'true');
  } else {
    state.isAdmin = false;
    localStorage.setItem(IS_ADMIN, 'false');
  }

  user.role = roleOf(user);
};

const appSlice = createSlice({
  name: 'appSlice',
  initialState: initialState,
  reducers: {
    onChangeLoading: (state, action) => {
      state.appLoading = get(action, 'payload.loading', false);
    },
    onChangeDashboardLoading: (state, action) => {
      state.appLoading = get(action, 'payload.loading', false);
    },
    clearMessage: (state) => {
      state.messageNotification = null;
    },
    /**
     * Stores a freshly minted token and nothing else (NX-5238).
     *
     * The idle-session renewal runs on a timer in the background and has only a
     * new token to record. applyLoginAsState would do that, but it also rebuilds
     * the user, the role flags, payrollCountry and policies out of
     * localStorage and re-applies isSuperAdmin from ADMIN_LOGIN_AS — so anything
     * held in the store but not written back to localStorage would be silently
     * reverted by a renewal the user never asked for.
     */
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      localStorage.setItem(TOKEN, action.payload);
    },
    applyLoginAsState: (state) => {
      const token = localStorage.getItem(TOKEN) || '';
      const user = JSON.parse(localStorage.getItem(USER) || 'null');
      const supperAdmin =
        JSON.parse(localStorage.getItem(ADMIN_LOGIN_AS) || '{}') || null;
      if (token && user) {
        applyRoleFlags(state, user);
        state.token = token;
        state.user = {
          ...user,
          ...(supperAdmin?.user
            ? {
                isSuperAdmin: true,
                superAdminEmail:
                  supperAdmin.user?.email ||
                  supperAdmin.user?.username ||
                  '',
              }
            : {}),
        };
        state.payrollCountry =
          user.payrollCountry || user.companyParent?.payrollCountry || '';
        const policies: string[] = user?.policies || [];
        if (policies.length) {
          state.policies = policies;
        }
      }
    },
    setOnboardingData: (state, action) => {
      const { onboarding, prospect, prospectId } = action.payload;
      state.onboarding = onboarding;
      state.prospect = prospect;
      state.prospectId = prospectId;
    },
    setIsBureau: (state, action) => {
      state.isBureau = action.payload;
      localStorage.setItem(IS_BUREAU, JSON.stringify(!!action.payload));
    },
    setPayrollCountry: (
      state,
      action: PayloadAction<PayrollCountryType>,
    ) => {
      state.payrollCountry = action.payload;
      localStorage.setItem(PAYROLL_COUNTRY, action.payload);
    },
    setAuthCode: (state, action) => {
      state.authCode = action.payload;
      localStorage.setItem(AUTH_CODE, JSON.stringify(action.payload));
      if (!action.payload) {
        localStorage.removeItem(AUTH_CODE_IS_BUREAU);
      }
    },
    // The issuing system must be written together with the code, never
    // separately; see resolveAuthTarget.
    setHandoffAuth: (
      state,
      action: PayloadAction<{ authCode: string; isBureau: boolean }>,
    ) => {
      const { authCode, isBureau } = action.payload;
      state.authCode = authCode;
      state.isBureau = isBureau;
      localStorage.setItem(AUTH_CODE, JSON.stringify(authCode));
      localStorage.setItem(AUTH_CODE_IS_BUREAU, String(isBureau));
      localStorage.setItem(IS_BUREAU, String(isBureau));
    },
    savePreviousAuthState: (state) => {
      const previousState = {
        token: state.token,
        user: state.user,
        isBureau: state.isBureau,
        isAdmin: state.isAdmin,
        isBureauEmployer: state.isBureauEmployer,
        isAgency: state.isAgency,
        isEmployee: state.isEmployee,
        payrollCountry: state.payrollCountry,
        policies: state.policies,
        actions: state.actions,
        enforcedModules: state.enforcedModules,
      };
      localStorage.setItem(PREVIOUS_AUTH_STATE, JSON.stringify(previousState));
    },
    restorePreviousAuthState: (state) => {
      const saved = localStorage.getItem(PREVIOUS_AUTH_STATE);
      if (!saved) return;
      const previous = JSON.parse(saved);

      state.token = previous.token || '';
      state.user = previous.user || null;
      state.isBureau = previous.isBureau ?? false;
      state.isAdmin = previous.isAdmin ?? false;
      state.isBureauEmployer = previous.isBureauEmployer ?? false;
      state.isAgency = previous.isAgency ?? false;
      state.isEmployee = previous.isEmployee ?? false;
      state.payrollCountry = previous.payrollCountry || '';
      state.policies = previous.policies || [];
      // Nothing is refetched on the way back, so the actions come back with the
      // session. A state saved without them hides what the actions gate.
      const access = toActionAccess({
        actions: previous.actions,
        enforcedModules: previous.enforcedModules ?? state.enforcedModules,
      });
      state.actions = access.actions;
      state.enforcedModules = access.enforcedModules;

      localStorage.setItem(TOKEN, previous.token || '');
      localStorage.setItem(USER, JSON.stringify(previous.user || null));
      localStorage.setItem(
        IS_BUREAU,
        JSON.stringify(previous.isBureau ?? false),
      );
      localStorage.setItem(IS_ADMIN, String(previous.isAdmin ?? false));
      localStorage.setItem(
        IS_BUREAU_EMPLOYER,
        String(previous.isBureauEmployer ?? false),
      );
      localStorage.setItem(IS_AGENCY, String(previous.isAgency ?? false));
      localStorage.setItem(IS_EMPLOYEE, String(previous.isEmployee ?? false));
      localStorage.setItem(PAYROLL_COUNTRY, previous.payrollCountry || '');
      localStorage.setItem(
        POLICIES,
        JSON.stringify(previous.policies || []),
      );
      localStorage.setItem(ME_ACTIONS, JSON.stringify(access.actions));
      localStorage.setItem(
        ENFORCED_MODULES,
        JSON.stringify(access.enforcedModules),
      );

      localStorage.removeItem(PREVIOUS_AUTH_STATE);
    },
    setLoginData: (state, action: PayloadAction<SetLoginDataPayload>) => {
      const { token, user, isBureau } = action.payload;
      if (token) {
        applyRoleFlags(state, user);

        // The 2FA wizard re-installs the session mid-flight (enrolling retires
        // the token this session holds), and that response used to arrive with
        // no country — which blanked this and moved an Ireland user's whole app
        // to the UK. See resolvePayrollCountry (NX-5833).
        const payrollCountry = resolvePayrollCountry(user, {
          userId: state.user?.id,
          payrollCountry: state.payrollCountry,
        });

        state.token = token;
        state.user = user as unknown as UserType;
        state.isBureau = isBureau;
        state.payrollCountry = payrollCountry;
        // Clear stale alternate session on fresh login

        localStorage.setItem(TOKEN, token);
        localStorage.setItem(USER, JSON.stringify(user));
        localStorage.setItem(IS_BUREAU, JSON.stringify(isBureau));
        localStorage.setItem(PAYROLL_COUNTRY, payrollCountry);
      }
    },
    setAlternateSystem: (
      state,
      action: PayloadAction<{ token: string; user: UserType; isBureau: boolean } | null>,
    ) => {
      state.alternateSystem = action.payload;
      if (action.payload) {
        localStorage.setItem(ALTERNATE_SESSION, JSON.stringify(action.payload));
      } else {
      }
    },
    switchSystem: (state) => {
      const alternate = state.alternateSystem;
      if (!alternate) return;

      const current = { token: state.token, user: state.user!, isBureau: state.isBureau };

      applyRoleFlags(state, alternate.user);
      state.token = alternate.token;
      state.user = alternate.user;
      state.isBureau = alternate.isBureau;
      state.alternateSystem = current;
      // The alternate session authenticates with its own token; keeping the
      // handoff code would send the previous system's user id to this one.
      state.authCode = '';

      localStorage.setItem(AUTH_CODE, JSON.stringify(''));
      localStorage.removeItem(AUTH_CODE_IS_BUREAU);
      localStorage.setItem(TOKEN, alternate.token);
      localStorage.setItem(USER, JSON.stringify(alternate.user));
      localStorage.setItem(IS_BUREAU, JSON.stringify(alternate.isBureau));
      localStorage.setItem(ALTERNATE_SESSION, JSON.stringify(current));
    },
  },
  extraReducers: (builder) => {
    builder.addCase(appAct.login.fulfilled, (state, { payload }) => {
      const token = payload?.data?.token;
      const user = payload?.data?.user;
      if (token) {
        applyRoleFlags(state, user);

        state.token = token;
        state.user = user as unknown as UserType;
        state.payrollCountry = user.payrollCountry || '';

        localStorage.setItem(TOKEN, token);
        localStorage.setItem(USER, JSON.stringify(user));
        localStorage.setItem(PAYROLL_COUNTRY, user.payrollCountry || '');
      }
    });
    builder.addCase(
      appAct.loginAsAccount.fulfilled,
      (state, { payload = {} }) => {
        const token = payload?.data?.token;
        const user = payload?.data?.user;
        const currentUser = {
          token: state?.token || '',
          user: state?.user || {},
        };
        if (token) {
          // Only save to localStorage so subsequent API calls can use the new token.
          // Do NOT update Redux state here to avoid flipping isAuthenticated
          // which causes premature route re-evaluation and auto-navigation.
          // Redux state will be applied later via applyLoginAsState().
          const policies: string[] = user?.policies || [];
          if (policies.length) {
            localStorage.setItem(POLICIES, JSON.stringify(policies));
          }

          localStorage.setItem(TOKEN, token);
          localStorage.setItem(USER, JSON.stringify(user));
          localStorage.setItem(ADMIN_LOGIN_AS, JSON.stringify(currentUser));
          localStorage.setItem(
            PAYROLL_COUNTRY,
            user.payrollCountry || user.companyParent?.payrollCountry || '',
          );
          // Drop the super admin's auth_code so the axios interceptor switches to
          // the impersonated user's bearer token. Without this, /me keeps resolving
          // to the super admin and the route guard 403s on /dashboards.
          localStorage.setItem(AUTH_CODE, JSON.stringify(''));
          localStorage.removeItem(AUTH_CODE_IS_BUREAU);
          state.authCode = '';
        }
      },
    );
    builder.addCase(
      appAct.loginAsByParentCompany.fulfilled,
      (state, { payload = {} }) => {
        const token = payload?.data?.token;
        const user = payload?.data?.user;
        const currentUser = {
          token: state?.token || '',
          user: state?.user || {},
        };
        if (token) {
          // Only save to localStorage, same as loginAsAccount.
          // Redux state will be applied later via applyLoginAsState().
          localStorage.setItem(TOKEN, token);
          localStorage.setItem(USER, JSON.stringify(user));
          localStorage.setItem(ADMIN_LOGIN_AS, JSON.stringify(currentUser));
          localStorage.setItem(
            PAYROLL_COUNTRY,
            user.companyParent?.payrollCountry || '',
          );
          localStorage.setItem(AUTH_CODE, JSON.stringify(''));
          localStorage.removeItem(AUTH_CODE_IS_BUREAU);
          state.authCode = '';
        }
      },
    );
    builder.addCase(appAct.getMe.fulfilled, (state, { payload = {} }) => {
      const supperAdmin =
        JSON.parse(localStorage.getItem(ADMIN_LOGIN_AS) || '{}') || null;
      const user = payload?.data?.data;
      const actionAuthor = user?.actionAuthor;
      if (user) {
        applyRoleFlags(state, user);

        const mergedUser = {
          ...user,
          ...(supperAdmin?.user
            ? {
                isSuperAdmin: true,
                superAdminEmail:
                  supperAdmin.user?.email ||
                  supperAdmin.user?.username ||
                  actionAuthor?.email ||
                  '',
              }
            : {}),
        };
        state.user = mergedUser;
        const serverCountry =
          user.payrollCountry || user.companyParent?.payrollCountry || '';
        state.payrollCountry = serverCountry;
        localStorage.setItem(PAYROLL_COUNTRY, serverCountry);
        localStorage.setItem(USER, JSON.stringify(mergedUser));
      }
    });
    builder.addCase(appAct.getMe.rejected, (state) => {
      state.authCode = '';
      state.user = null;
      localStorage.removeItem(AUTH_CODE);
      localStorage.removeItem(AUTH_CODE_IS_BUREAU);
      localStorage.removeItem(USER);
    });
    builder.addCase(
      appAct.getAppConfigs.fulfilled,
      (state, { payload = {} }) => {
        const configs = payload?.data || {};
        state.appConfigs = configs;
        localStorage.setItem(APP_CONFIGS, JSON.stringify(configs));
        appConfigManager.setConfigs(configs);
      },
    );
    builder.addCase(
      appAct.getPolicies.fulfilled,
      (state, { payload = {} }) => {
        const data = payload?.data?.data || [];
        localStorage.setItem(POLICIES, JSON.stringify(data));
        state.policies = data;
      },
    );
    builder.addCase(
      appAct.getMyActions.fulfilled,
      (state, { payload = {} }) => {
        const { actions, enforcedModules } = toActionAccess(
          payload?.data?.data,
        );
        state.actions = actions;
        state.enforcedModules = enforcedModules;
        localStorage.setItem(ME_ACTIONS, JSON.stringify(actions));
        localStorage.setItem(ENFORCED_MODULES, JSON.stringify(enforcedModules));
      },
    );
    // No action held and no module enforced, whatever the last answer stored:
    // menus, routes and buttons go by the screen policies, as before
    // /me/actions, on a fresh sign-in and on a reload alike. The server still
    // decides.
    builder.addCase(appAct.getMyActions.rejected, (state) => {
      state.actions = [];
      state.enforcedModules = [];
      localStorage.setItem(ME_ACTIONS, JSON.stringify([]));
      localStorage.setItem(ENFORCED_MODULES, JSON.stringify([]));
    });
    builder.addCase(appAct.logout.fulfilled, (state) => {
      localStorage.clear();
      // Every sign-out passes through here, so this is where the reason for the
      // last one stops applying (NX-5238). An idle sign-out records its reason
      // after this has run; anything else — the user's own Logout, portal access
      // being withdrawn — leaves the login screen with nothing to announce.
      try {
        sessionStorage.removeItem(SESSION_EXPIRED_KEY);
      } catch {
        // Private-mode storage failures must not stop the sign-out itself.
      }
      state.token = '';
      state.user = null;
      // A hand-off session's code went with the storage above; left in the
      // store, the signed-out page would still count as that session (NX-6269).
      state.authCode = '';
      state.actions = [];
      state.enforcedModules = [];
    });
    //sign up
    builder.addCase(appAct.signup.pending, (state) => {
      state.appLoading = true;
    });
    builder.addCase(appAct.signup.fulfilled, (state, { payload = {} }) => {
      state.appLoading = false;
      const token = payload?.data?.token;
      const user = payload?.data?.user || {};
      if (token) {
        applyRoleFlags(state, user);

        state.token = token;
        state.user = user;
        state.payrollCountry = user.payrollCountry || '';

        localStorage.setItem(TOKEN, token);
        localStorage.setItem(USER, JSON.stringify(user));
        localStorage.setItem(PAYROLL_COUNTRY, user.payrollCountry || '');
      }
    });
    builder.addCase(appAct.signup.rejected, (state, action) => {
      state.appLoading = false;
      const message =
        (action.payload as any)?.response?.data?.message ||
        "We're sorry, but something went wrong. Please try again.";
      state.messageNotification = {
        text: Array.isArray(message) ? message[0] : message || '',
        type: 'error',
      };
    });
    //onboarding
    builder.addCase(
      appAct.getOnboarding.fulfilled,
      (state, { payload = {} }) => {
        const completed = payload?.data?.completed || false;
        const onboarding = payload?.data?.data?.onboarding || {};
        const prospect = payload?.data?.data?.prospectOnboarding || {};
        prospect.completed = completed;

        state.onboarding = onboarding;
        state.prospect = { ...state.prospect, ...prospect };
      },
    );
    builder.addCase(
      appAct.createOnboarding.fulfilled,
      (state, { payload = {} }) => {
        const onboarding = payload?.data?.data?.onboarding || {};
        const onboardingTasks = payload?.data?.data?.onboardingTasks || {};
        const prospect = payload?.data?.data?.prospectOnboarding || {};
        prospect.onboardingTasks = onboardingTasks;

        state.onboarding = onboarding;
        state.prospect = { ...state.prospect, ...prospect };
      },
    );
    builder.addCase(
      appAct.updateOnboarding.fulfilled,
      (state, { payload = {} }) => {
        const completed = payload?.data?.completed || false;
        const onboarding = payload?.data?.data?.onboarding || {};
        const onboardingTasks = payload?.data?.data?.onboardingTasks || {};
        const prospect = payload?.data?.data?.prospectOnboarding || {};
        prospect.onboardingTasks = onboardingTasks;
        prospect.completed = completed;

        state.onboarding = onboarding;
        state.prospect = { ...state.prospect, ...prospect };
      },
    );
    builder.addCase(
      appAct.updateOnboardingNationality.fulfilled,
      (state, { payload = {} }) => {
        const completed = payload?.data?.completed || false;
        const onboarding = payload?.data?.data?.onboarding || {};
        const onboardingTasks = payload?.data?.data?.onboardingTasks || {};
        const prospect = payload?.data?.data?.prospectOnboarding || {};
        prospect.onboardingTasks = onboardingTasks;
        prospect.completed = completed;

        state.onboarding = onboarding;
        state.prospect = { ...state.prospect, ...prospect };
      },
    );
    builder.addCase(
      appAct.updateOnboardingVerifyId.fulfilled,
      (state, { payload = {} }) => {
        const completed = payload?.data?.completed || false;
        const onboarding = payload?.data?.data?.onboarding || {};
        const onboardingTasks = payload?.data?.data?.onboardingTasks || {};
        const prospect = payload?.data?.data?.prospectOnboarding || {};
        prospect.onboardingTasks = onboardingTasks;
        prospect.completed = completed;

        state.onboarding = onboarding;
        state.prospect = { ...state.prospect, ...prospect };
      },
    );
    builder.addCase(
      appAct.getStatusZoho.fulfilled,
      (state, { payload = {} }) => {
        const zohoSignStatus = payload.data?.status;
        const zohoDocumentExpired = payload.data?.zohoDocumentExpired;
        const zohoDocumentDeleted = payload.data?.zohoDocumentDeleted;
        const prospect = payload?.data?.data?.prospectOnboarding || {};
        prospect.zohoSignStatus = zohoSignStatus;
        prospect.zohoDocumentExpired = zohoDocumentExpired;
        prospect.zohoDocumentDeleted = zohoDocumentDeleted;

        state.prospect = { ...state.prospect, ...prospect };
      },
    );
    builder.addCase(
      appAct.updateOnboardingSuccess.fulfilled,
      (state, { payload = {} }) => {
        const completed = payload?.data?.completed || false;
        const onboarding = payload?.data?.data?.onboarding || {};
        const onboardingTasks = payload?.data?.data?.onboardingTasks || {};
        const prospect = payload?.data?.data?.prospectOnboarding || {};
        prospect.subcontractor = payload?.data?.data?.subcontractor || {};
        prospect.onboardingTasks = onboardingTasks;
        prospect.completed = completed;

        state.onboarding = onboarding;
        state.prospect = { ...state.prospect, ...prospect };

        // Release the employee from the onboarding-only routing lock. Every step
        // can be saved while the ID / share-code check is still pending, so the
        // onboarding is only really finished once that check is DONE — until
        // then they stay on the onboarding page.
        const isIdVerified =
          onboarding?.verifiedIdOrCodeStatus ===
          VerifiedIdOrCodeStatusEnum.DONE;
        if (isIdVerified && state.user) {
          const mergedUser = { ...state.user, isOnboarding: false };
          state.user = mergedUser;
          localStorage.setItem(USER, JSON.stringify(mergedUser));
        }
      },
    );
    // -------
    builder.addCase(
      appAct.getAccountList.fulfilled,
      (state, { payload = {} }) => {
        state.acountList = payload?.data || {};
      },
    );
    //bureau settings
    builder.addCase(appAct.getBureauSettings.pending, (state) => {
      state.appLoading = true;
    });
    builder.addCase(appAct.getBureauSettings.fulfilled, (state) => {
      state.appLoading = false;
    });
    builder.addCase(appAct.getBureauSettings.rejected, (state) => {
      state.appLoading = false;
    });
    //update
    builder.addCase(appAct.updateBureauSettings.pending, (state) => {
      state.appLoading = true;
    });
    builder.addCase(appAct.updateBureauSettings.fulfilled, (state) => {
      state.appLoading = false;
    });
    builder.addCase(appAct.updateBureauSettings.rejected, (state) => {
      state.appLoading = false;
    });
    //logout supper admin
    builder.addCase(appAct.logoutSupperAdmin.pending, (state) => {
      state.appLoading = true;
    });
    builder.addCase(appAct.logoutSupperAdmin.fulfilled, (state) => {
      state.appLoading = false;
    });
    builder.addCase(appAct.logoutSupperAdmin.rejected, (state) => {
      state.appLoading = false;
    });
    //change country
    builder.addCase(
      appAct.switchCountry.fulfilled,
      (state, { payload = {} }) => {
        const token = payload?.data?.token;
        const user = payload?.data?.user;
        if (token) {
          applyRoleFlags(state, user);

          state.token = token;
          state.user = user;
          state.payrollCountry = user.payrollCountry || '';

          localStorage.setItem(TOKEN, token);
          localStorage.setItem(USER, JSON.stringify(user));
          localStorage.setItem(PAYROLL_COUNTRY, user.payrollCountry || '');
        }
      },
    );
    builder.addCase(
      appAct.updateOnboardingUserByAdmin.fulfilled,
      (state, { payload = {} }) => {
        const completed = payload?.data?.completed || false;
        const onboarding = payload?.data?.data?.onboarding || {};
        const onboardingTasks = payload?.data?.data?.onboardingTasks || {};
        const prospect = payload?.data?.data?.prospectOnboarding || {};
        prospect.onboardingTasks = onboardingTasks;
        prospect.completed = completed;

        state.onboarding = onboarding;
        state.prospect = { ...state.prospect, ...prospect };
      },
    );
    builder.addCase(
      appAct.createOnboardingUserByAdmin.fulfilled,
      (state, { payload = {} }) => {
        const completed = payload?.data?.completed || false;
        const onboarding = payload?.data?.data?.onboarding || {};
        const onboardingTasks = payload?.data?.data?.onboardingTasks || {};
        const prospect = payload?.data?.data?.prospectOnboarding || {};
        prospect.onboardingTasks = onboardingTasks;
        prospect.completed = completed;

        state.onboarding = onboarding;
        state.prospect = { ...state.prospect, ...prospect };
      },
    );
    builder.addCase(
      appAct.updateOnboardingSuccessByAdmin.fulfilled,
      (state, { payload = {} }) => {
        const completed = payload?.data?.completed || false;
        const onboarding = payload?.data?.data?.onboarding || {};
        const onboardingTasks = payload?.data?.data?.onboardingTasks || {};
        const prospect = payload?.data?.data?.prospectOnboarding || {};
        prospect.onboardingTasks = onboardingTasks;
        prospect.completed = completed;

        state.onboarding = onboarding;
        state.prospect = { ...state.prospect, ...prospect };
      },
    );

    // Process Year End actions
    builder.addCase(appAct.processYearEnd.pending, (state) => {
      state.appLoading = true;
    });
    builder.addCase(
      appAct.processYearEnd.fulfilled,
      (state, { payload = {} }) => {
        state.appLoading = false;
        state.currentYearEndProcess = payload?.data?.data || null;
      },
    );
    builder.addCase(appAct.processYearEnd.rejected, (state) => {
      state.appLoading = false;
    });

    // Get Current Year End Process actions
    builder.addCase(appAct.getCurrentYearEndProcess.pending, (state) => {
      state.appLoading = true;
    });
    builder.addCase(
      appAct.getCurrentYearEndProcess.fulfilled,
      (state, { payload = {} }) => {
        state.appLoading = false;
        state.currentYearEndProcess = payload?.data || null;
      },
    );
    builder.addCase(appAct.getCurrentYearEndProcess.rejected, (state) => {
      state.appLoading = false;
    });
  },
});

export const appReducer = appSlice.reducer;
export const appActions = {
  ...appSlice.actions,
  ...appAct,
};
